import { useState } from 'react';
import {
  CalendarDays,
  CalendarRange,
  Check,
  ChevronDown,
  Gauge,
  LifeBuoy,
  Minus,
  ShieldCheck,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { UpsellPaymentAssistance, UpsellPaymentContractSummary } from '../api/paymentApi';
import { formatDays, formatEuroCap, formatKm, formatLongDate, formatMonths } from '../utils/paymentDisplay';
import { IconBadge } from './PaymentUi';

const SECTION_CLASS = 'tw-border-t tw-border-slate-100 tw-px-5 tw-py-5 sm:tw-px-6';

/** Durée, kilométrage plafond, plafond financier, date de début. */
export function CoverageFacts({ summary }: { summary: UpsellPaymentContractSummary }) {
  const facts: Array<{ icon: LucideIcon; label: string; value: string }> = [];
  if (summary.duration != null && summary.duration > 0) {
    facts.push({ icon: CalendarRange, label: 'Durée', value: formatMonths(summary.duration) });
  }
  if (summary.kmLimit != null && summary.kmLimit > 0) {
    facts.push({ icon: Gauge, label: 'Kilométrage plafond', value: formatKm(summary.kmLimit) });
  }
  if (summary.financialCap != null && summary.financialCap > 0) {
    facts.push({ icon: ShieldCheck, label: 'Plafond financier', value: formatEuroCap(summary.financialCap) });
  }
  const startDate = formatLongDate(summary.startDate);
  if (startDate) {
    facts.push({ icon: CalendarDays, label: 'Date de début', value: startDate });
  }

  if (facts.length === 0) return null;

  return (
    <div className={SECTION_CLASS}>
      <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
        {facts.map((fact) => (
          <div
            key={fact.label}
            className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-slate-200 tw-px-3 tw-py-3"
          >
            <IconBadge icon={fact.icon} size="sm" />
            <div className="tw-min-w-0">
              <p className="tw-mb-0 tw-text-xs tw-text-gray-600">{fact.label}</p>
              <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-gray-900">{fact.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Pièces couvertes : nombre, catégories, liste dépliable groupée par catégorie. */
export function CoveredParts({ summary }: { summary: UpsellPaymentContractSummary }) {
  const [open, setOpen] = useState(false);
  const groups = summary.partsByCategory.filter((group) => group.parts.length > 0);
  const count = summary.partsCount;
  const plural = count > 1 ? 's' : '';

  return (
    <div className={SECTION_CLASS}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
          <IconBadge icon={Wrench} size="sm" />
          <div className="tw-min-w-0">
            <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-gray-900">Pièces couvertes</h3>
            <p className="tw-mb-0 tw-text-sm tw-text-gray-600">
              {count > 0
                ? `${count} pièce${plural} couverte${plural}`
                : 'Selon les conditions générales de votre contrat'}
            </p>
          </div>
        </div>
        {groups.length > 0 && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls="covered-parts-list"
            onClick={() => setOpen((value) => !value)}
            className="tw-inline-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-[color:var(--pay-ink)] tw-transition-colors hover:tw-bg-slate-50"
          >
            {open ? 'Masquer' : 'Voir la liste'}
            <ChevronDown
              className={`tw-h-4 tw-w-4 tw-transition-transform ${open ? 'tw-rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      {groups.length > 0 && !open && (
        <ul className="tw-mb-0 tw-mt-3 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-pl-0">
          {groups.map((group) => (
            <li
              key={group.category}
              className="tw-rounded-full tw-bg-slate-100 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-700"
            >
              {group.category}
            </li>
          ))}
        </ul>
      )}

      {groups.length > 0 && open && (
        <div id="covered-parts-list" className="tw-mt-4 tw-space-y-4">
          {groups.map((group) => (
            <div key={group.category}>
              <p className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-600">
                {group.category}
                <span className="tw-ml-1 tw-font-normal tw-normal-case tw-tracking-normal">({group.parts.length})</span>
              </p>
              <ul className="tw-mb-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-x-4 tw-gap-y-1.5 tw-pl-0 sm:tw-grid-cols-2">
                {group.parts.map((part, index) => (
                  <li key={`${part}-${index}`} className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-gray-700">
                    <Check className="tw-mt-0.5 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-[color:var(--pay-ink)]" aria-hidden="true" />
                    {part}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function YesNo({ yes, label }: { yes: boolean; label: string }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-gray-900">
      {yes ? (
        <Check className="tw-h-4 tw-w-4 tw-text-emerald-600" aria-hidden="true" />
      ) : (
        <Minus className="tw-h-4 tw-w-4 tw-text-gray-400" aria-hidden="true" />
      )}
      {label}
    </span>
  );
}

/** Assistance affichée explicitement : chaque prestation est « Oui » ou « Non », jamais vide. */
export function AssistanceDetails({ assistance }: { assistance: UpsellPaymentAssistance }) {
  const loanerLabel = assistance.loanerVehicle
    ? assistance.loanerDays > 0
      ? `Oui (${formatDays(assistance.loanerDays)})`
      : 'Oui'
    : 'Non';

  return (
    <div className={SECTION_CLASS}>
      <div className="tw-flex tw-items-center tw-gap-3">
        <IconBadge icon={LifeBuoy} size="sm" />
        <div>
          <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-gray-900">Assistance</h3>
          <p className="tw-mb-0 tw-text-sm tw-text-gray-600">
            {assistance.included ? 'Incluse dans votre garantie' : 'Non incluse dans cette garantie'}
          </p>
        </div>
      </div>

      <div className="tw-mt-3 tw-divide-y tw-divide-slate-100 tw-rounded-xl tw-border tw-border-slate-200 tw-px-4">
        {assistance.label && (
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2.5">
            <span className="tw-text-sm tw-text-gray-600">Formule</span>
            <span className="tw-text-right tw-text-sm tw-font-medium tw-text-gray-900">{assistance.label}</span>
          </div>
        )}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2.5">
          <span className="tw-text-sm tw-text-gray-600">Dépannage / remorquage</span>
          <YesNo yes={assistance.breakdown} label={assistance.breakdown ? 'Oui' : 'Non'} />
        </div>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2.5">
          <span className="tw-text-sm tw-text-gray-600">Véhicule de prêt</span>
          <YesNo yes={assistance.loanerVehicle} label={loanerLabel} />
        </div>
        {assistance.benefits.map((benefit, index) => (
          <div key={`${benefit.label}-${index}`} className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2.5">
            <span className="tw-text-sm tw-text-gray-600">{benefit.label}</span>
            <span className="tw-text-right tw-text-sm tw-font-medium tw-text-gray-900">{benefit.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
